import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { Device, from, until } from '@helpers/media';
import { h1 } from '@helpers/typography';
import MotionTrack from '@stories/Components/Misc/Carousels/Carousel/MotionTrack/MotionTrack';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { PaginationStyles } from '@stories/Components/Pagination/Pagination.styles';
import { transparentize } from 'polished';
import styled, { keyframes } from 'styled-components';
const Container = styled(SectionWrapper) `
  --bg: ${brand.primary.blue.base};
  --fg: ${brand.white};

  &[data-theme='white'] {
    --bg: ${brand.white};
    --fg: ${brand.primary.blue.base};
  }

  text-align: center;
  background-color: var(--bg);
  color: var(--fg);
`;
const Title = styled.h2 `
  ${h1};

  color: var(--fg);
`;
const mediaItemShimmer = keyframes `
  0% {
    left: -150%;
  }
  100% {
    left: 150%;
  }
`;
const MediaItem = styled.div `
  position: relative;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.1);
  [data-theme='white'] & {
    background-color: ${transparentize(0.1, brand.primary.blue.base)};
  }

  display: flex;

  > audio {
    margin: auto;
  }

  &:has(audio) {
    &::before {
      animation: unset;
      background: ${transparentize(0.5, brand.black)};
      left: 0;
    }
  }

  &:before {
    --bgColour: rgba(255, 255, 255, 0.15);
    content: '';
    position: absolute;
    top: 0;
    left: -150%;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent 0%, var(--bgColour) 50%, transparent 100%);
    animation: ${mediaItemShimmer} 1.5s infinite;
  }

  [data-theme='white'] & {
    &::before {
      --bgColour: ${transparentize(0.15, brand.primary.blue.base)};
    }
  }
`;
const Image = styled.img `
  position: relative;
  ${imageCoverContainer(true)};
`;
const Video = styled.video `
  position: relative;
  ${imageCoverContainer(true)};
`;
const PlayButton = styled.button `
  --size: ${fluid(32, 44)};
  --iconSize: ${fluid(10, 16)};
  ${transition('background-color')};

  position: absolute;
  bottom: 12px;
  right: 12px;
  width: var(--size);
  height: var(--size);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 100%;
  background-color: rgba(217, 217, 217, 0.5);

  backdrop-filter: blur(13px);

  &:hover,
  &:focus-visible {
    background-color: rgba(150, 150, 150, 0.75);
  }

  svg {
    position: relative;
    width: var(--iconSize);
    height: var(--iconSize);
    left: 2px;
    color: ${brand.white};
  }
`;
const CarouselLayout = styled.div `
  overflow: hidden;
  margin-top: 24px;
`;
const CarouselWrapper = styled.div `
  --itemPeek: 0;
  --itemGutter: ${fluid(16, 24)};
  --visibleItems: 1;

  margin: 0 calc(var(--itemGutter) * -0.5);
`;
const Track = styled(MotionTrack) ``;
const Item = styled.li `
  display: grid;
  gap: ${fluid(8, 24)};
  padding: 0 calc(var(--itemGutter) / 2);
  min-width: 100%;
  overflow: hidden;
`;
const Pagination = styled.div `
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 38px;

  ${PaginationStyles.Container} {
    margin-top: ${fluid(20, 32, Device.Mobile, Device.TabletLarge)};
  }
`;
const DesktopLayout = styled.div `
  display: contents;

  ${Item} {
    grid-template-columns: repeat(16, 1fr);
    flex: 0 0 calc(100% / var(--visibleItems) - (var(--itemPeek, 0px) / var(--visibleItems)));

    &:first-child,
    &:nth-child(4n + 1) {
      > ${MediaItem} {
        grid-column: span 4;
        aspect-ratio: 342 / 287;

        &:first-child {
          grid-column: span 8;
          grid-row: span 2;
        }
      }
    }

    &:nth-child(4n + 2) {
      > ${MediaItem} {
        grid-column: span 4;
      }
    }

    &:nth-child(4n + 3) {
      grid-template-rows: repeat(3, 1fr);

      > ${MediaItem} {
        &:first-child {
          grid-area: 1 / 1 / 3 / 7;
        }
        &:nth-child(2) {
          grid-area: 1 / 7 / 2 / 13;
        }
        &:nth-child(3) {
          grid-area: 1 / 13 / 2 / 17;
        }
        &:nth-child(4) {
          grid-area: 3 / 1 / 4 / 7;
        }
        &:nth-child(5) {
          grid-area: 2 / 7 / 4 / 13;
        }
        &:nth-child(6) {
          grid-area: 2 / 13 / 3 / 17;
        }
        &:nth-child(7) {
          grid-area: 3 / 13 / 4 / 17;
        }
      }
    }

    &:nth-child(4n) {
      > ${MediaItem} {
        grid-template-rows: repeat(3, 1fr);

        &:nth-child(-n + 4) {
          grid-row: 1 / 2;
          grid-column: span 4;
        }

        &:nth-child(5),
        &:nth-child(6) {
          grid-column: span 8;
          grid-row: span 3;
        }
      }
    }

    ${MediaItem} {
      @media ${until(Device.TabletMedium)} {
        grid-column: span 8 !important;
        grid-row: span 1 !important;
      }
    }
  }

  @media ${until(Device.TabletMedium)} {
    display: none;
  }
`;
const MobileLayout = styled.div `
  display: contents;

  ${Item} {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: min-content;

    ${MediaItem} {
      height: ${fluid(120, 360)};
    }

    &:first-child {
      ${MediaItem} {
        height: auto;

        &:first-child {
          grid-column: span 2;
        }
      }
    }

    &:last-child {
      ${MediaItem} {
        &:first-child,
        &:nth-child(odd) {
          &:last-child {
            grid-column: span 2;
          }
        }
      }
    }
  }

  @media ${from(Device.TabletMedium)} {
    display: none;
  }
`;
export const MediaGalleryStyles = {
    CarouselLayout,
    CarouselWrapper,
    Container,
    DesktopLayout,
    Image,
    Item,
    MediaItem,
    MobileLayout,
    Pagination,
    PlayButton,
    Title,
    Track,
    Video,
};
